import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "react-oidc-context";

import {
  createDraft,
  deleteDraft,
  listMyDrafts,
  updateDraft,
} from "@/api/drafts";
import type { TranslationDraft } from "@/api/types";
import { useToast } from "@/components/toast";
import { currentDraftTitle, draftTitle } from "./draftTitle";

const PAGE_SIZE = 20;

const STATUS_RU: Record<string, string> = {
  open: "Открыт",
  saved: "Сохранён",
  sent_to_review: "На ревью",
  released: "Выпущен",
  discarded: "Удалён",
};

function statusBadgeClass(status: string): string {
  switch (status) {
    case "released":
      return "mt-badge--success";
    case "sent_to_review":
      return "mt-badge--warning";
    case "discarded":
      return "mt-badge--danger";
    case "saved":
      return "mt-badge--info";
    default:
      return "mt-badge--neutral";
  }
}

function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString("ru-RU", { dateStyle: "short", timeStyle: "short" });
}

function snippet(text: string | null, n = 70): string {
  const t = (text ?? "").trim();
  if (!t) return "—";
  return t.length > n ? `${t.slice(0, n)}…` : t;
}

export default function MyTranslations() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const auth = useAuth();
  const { toast } = useToast();
  const [offset, setOffset] = useState(0);
  const [showDiscarded, setShowDiscarded] = useState(false);

  const owner =
    (auth.user?.profile?.preferred_username as string | undefined) ??
    (auth.user?.profile?.email as string | undefined) ??
    "anonymous";

  const query = useQuery({
    queryKey: ["my-drafts", { offset, showDiscarded }],
    queryFn: async () => {
      const r = await listMyDrafts({
        limit: PAGE_SIZE,
        offset,
        include_discarded: showDiscarded,
      });
      if (!r.ok) throw new Error(r.error);
      return r.data;
    },
  });

  function refetchList(): void {
    void qc.invalidateQueries({ queryKey: ["my-drafts"] });
  }

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const r = await deleteDraft(id);
      if (!r.ok) throw new Error(r.error);
      return r.data.draft;
    },
    onSuccess: () => {
      toast("Перевод удалён", "success");
      refetchList();
    },
    onError: (e: Error) => toast(`Не удалось удалить: ${e.message}`, "error"),
  });

  const restoreMutation = useMutation({
    mutationFn: async (id: string) => {
      const r = await updateDraft(id, { status: "open" });
      if (!r.ok) throw new Error(r.error);
      return r.data.draft;
    },
    onSuccess: () => {
      toast("Перевод восстановлен", "success");
      refetchList();
    },
    onError: (e: Error) =>
      toast(`Не удалось восстановить: ${e.message}`, "error"),
  });

  const duplicateMutation = useMutation({
    mutationFn: async (d: TranslationDraft) => {
      const r = await createDraft({
        owner,
        source_lang: d.source_lang,
        target_lang: d.target_lang,
        source_text: d.source_text,
        target_text: d.target_text ?? "",
        status: "open",
        metadata: (d.metadata ?? {}) as Record<string, unknown>,
        pretranslate_snapshot: d.pretranslate_snapshot ?? undefined,
      });
      if (!r.ok) throw new Error(r.error);
      return r.data.draft;
    },
    onSuccess: (created) => {
      toast("Создана копия", "success");
      navigate(`/translate/${encodeURIComponent(created.draft_id)}`);
    },
    onError: (e: Error) =>
      toast(`Не удалось дублировать: ${e.message}`, "error"),
  });

  const renameMutation = useMutation({
    mutationFn: async (vars: {
      id: string;
      title: string;
      metadata: Record<string, unknown>;
    }) => {
      const r = await updateDraft(vars.id, {
        metadata: { ...vars.metadata, title: vars.title },
      });
      if (!r.ok) throw new Error(r.error);
      return r.data.draft;
    },
    onSuccess: () => {
      toast("Название сохранено", "success");
      refetchList();
    },
    onError: (e: Error) =>
      toast(`Не удалось переименовать: ${e.message}`, "error"),
  });

  function promptRename(d: TranslationDraft): void {
    const name = window.prompt("Название перевода:", currentDraftTitle(d));
    if (name == null) return;
    renameMutation.mutate({
      id: d.draft_id,
      title: name.trim(),
      metadata: (d.metadata ?? {}) as Record<string, unknown>,
    });
  }

  const busy =
    deleteMutation.isPending ||
    restoreMutation.isPending ||
    duplicateMutation.isPending ||
    renameMutation.isPending;

  const drafts = query.data?.drafts ?? [];
  const total = query.data?.total ?? 0;

  return (
    <div style={{ padding: "var(--sp-3)", maxWidth: 1100, margin: "0 auto" }}>
      <header
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          marginBottom: "var(--sp-3)",
          flexWrap: "wrap",
        }}
      >
        <h1 style={{ margin: 0, fontSize: "var(--fs-lg)" }}>Мои переводы</h1>
        <span className="mt-toolbar__sep" style={{ flex: 1 }} />
        <label
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            fontSize: "var(--fs-sm)",
            color: "var(--muted)",
          }}
        >
          <input
            type="checkbox"
            checked={showDiscarded}
            onChange={(e) => {
              setShowDiscarded(e.target.checked);
              setOffset(0);
            }}
          />
          Показать удалённые
        </label>
        <Link to="/translate" className="mt-btn mt-btn--primary mt-btn--sm">
          + Новый перевод
        </Link>
      </header>

      <div className="rl-tablewrap">
        {query.isLoading ? (
          <div className="mt-empty">Загружаем…</div>
        ) : query.isError ? (
          <div className="mt-empty mt-empty--error">
            {(query.error as Error).message}
          </div>
        ) : drafts.length === 0 ? (
          <div className="mt-empty">
            Пока нет переводов. Создайте первый — «+ Новый перевод».
          </div>
        ) : (
          <table className="mt-table">
            <thead>
              <tr>
                <th style={{ width: 150 }}>Обновлён</th>
                <th>Название</th>
                <th style={{ width: 80 }}>Языки</th>
                <th>Перевод</th>
                <th style={{ width: 110 }}>Статус</th>
                <th style={{ width: 300 }} />
              </tr>
            </thead>
            <tbody>
              {drafts.map((d) => (
                <tr
                  key={d.draft_id}
                  style={{ cursor: "pointer" }}
                  onClick={() =>
                    navigate(`/translate/${encodeURIComponent(d.draft_id)}`)
                  }
                >
                  <td className="mt-text-tight mt-muted">
                    {fmtDate(d.updated_at)}
                  </td>
                  <td>
                    <strong>{draftTitle(d)}</strong>
                  </td>
                  <td className="mt-mono mt-text-tight">
                    {d.source_lang}→{d.target_lang}
                  </td>
                  <td className="mt-muted">{snippet(d.target_text)}</td>
                  <td>
                    <span
                      className={
                        "mt-badge mt-badge--no-dot " +
                        statusBadgeClass(d.status)
                      }
                    >
                      {STATUS_RU[d.status] ?? d.status}
                    </span>
                  </td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <div
                      style={{
                        display: "flex",
                        gap: 4,
                        justifyContent: "flex-end",
                        flexWrap: "wrap",
                      }}
                    >
                      <button
                        className="mt-btn mt-btn--sm mt-btn--ghost"
                        type="button"
                        disabled={busy}
                        onClick={() => promptRename(d)}
                        title="Задать название"
                      >
                        Переименовать
                      </button>
                      <button
                        className="mt-btn mt-btn--sm mt-btn--ghost"
                        type="button"
                        disabled={busy}
                        onClick={() => duplicateMutation.mutate(d)}
                        title="Создать копию и открыть"
                      >
                        Дублировать
                      </button>
                      {d.status === "discarded" ? (
                        <button
                          className="mt-btn mt-btn--sm mt-btn--ghost"
                          type="button"
                          disabled={busy}
                          onClick={() => restoreMutation.mutate(d.draft_id)}
                          title="Вернуть из удалённых"
                        >
                          Восстановить
                        </button>
                      ) : (
                        <button
                          className="mt-btn mt-btn--sm mt-btn--ghost mt-btn--danger"
                          type="button"
                          disabled={busy}
                          onClick={() => {
                            if (window.confirm("Удалить этот перевод?")) {
                              deleteMutation.mutate(d.draft_id);
                            }
                          }}
                          title="Удалить (можно восстановить)"
                        >
                          Удалить
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="rl-foot" style={{ marginTop: "var(--sp-2)" }}>
        <div className="mt-pager">
          <button
            className="mt-icon-btn mt-icon-btn--bordered"
            onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            disabled={offset === 0}
            type="button"
            aria-label="Предыдущая"
          >
            ‹
          </button>
          <span className="mt-pager__count">
            {drafts.length === 0
              ? "0"
              : `${offset + 1} — ${offset + drafts.length}`}
            {total > 0 ? ` из ${total}` : ""}
          </span>
          <button
            className="mt-icon-btn mt-icon-btn--bordered"
            onClick={() => setOffset(offset + PAGE_SIZE)}
            disabled={offset + drafts.length >= total}
            type="button"
            aria-label="Следующая"
          >
            ›
          </button>
        </div>
        {query.isFetching && <span className="mt-spinner" />}
      </div>
    </div>
  );
}
